"use client";

import * as React from "react";
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { cn } from "@/lib/utils";
import {
  circleActionVariantCssVars,
  resolveCircleActionVariant,
  type CircleActionVariant,
} from "./circle-action-variants";
import {
  LiquidSurface,
  groupSquishStyle,
  shouldGroupSquish,
  type LiquidDeformAxis,
  type LiquidSurfaceBounceIntensity,
} from "./liquid-surface";

export interface LiquidPillGroupItem {
  value: string;
  label?: React.ReactNode;
  icon?: React.ReactNode;
  variant?: CircleActionVariant;
  disabled?: boolean;
}

export interface LiquidPillGroupProps {
  items: LiquidPillGroupItem[];
  activeValue?: string | null;
  onChange?: (value: string) => void;
  onHover?: (value: string | null) => void;
  size?: "sm" | "md";
  bounceIntensity?: LiquidSurfaceBounceIntensity;
  deformAxis?: LiquidDeformAxis;
  className?: string;
  /** Distribución semántica de cada opción. `equal` reserva el mismo ancho para controles tipo escala. */
  itemLayout?: "content" | "equal";
}

type BubbleRect = { left: number; width: number };

const SLIDE_MS = 560;
const SLIDE_EASE = "cubic-bezier(0.22,1,0.36,1)";
const SLIDE_TRANSITION = [
  `left ${SLIDE_MS}ms ${SLIDE_EASE}`,
  `width ${SLIDE_MS}ms ${SLIDE_EASE}`,
  "opacity 240ms ease",
].join(",");

export function LiquidPillGroup({
  items,
  activeValue,
  onChange,
  onHover,
  size = "sm",
  bounceIntensity = "strong",
  deformAxis = "horizontal",
  className,
  itemLayout = "content",
}: LiquidPillGroupProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const bubbleRef    = useRef<HTMLDivElement>(null);
  const itemRefs     = useRef<(HTMLButtonElement | null)[]>([]);
  const contentRefs  = useRef<(HTMLSpanElement | null)[]>([]);

  const [renderBubble, setRenderBubble] = useState<BubbleRect | null>(null);
  const [animate, setAnimate]           = useState(false);
  const [hoveredValue, setHoveredValue] = useState<string | null>(null);

  const prevBubbleRect    = useRef<BubbleRect | null>(null);
  const prevDisplayTarget = useRef<string | null>(null);

  const displayTarget = hoveredValue ?? activeValue ?? null;
  const displayItem = items.find((i) => i.value === displayTarget);
  const displayVariant = resolveCircleActionVariant(displayItem?.variant ?? "slate");

  const triggerBounce = useCallback(() => {
    if (!shouldGroupSquish(bounceIntensity)) return;
    const el = bubbleRef.current;
    if (!el) return;
    el.style.animation = "none";
    void el.offsetHeight;
    el.style.animation = "lpg-horizontal-bounce 560ms cubic-bezier(0.34,1.2,0.64,1) both";
  }, [bounceIntensity]);

  const measureAt = useCallback((target: string) => {
    const idx = items.findIndex((i) => i.value === target);
    const el  = itemRefs.current[idx];
    const contentEl = contentRefs.current[idx];
    const container = containerRef.current;
    if (!el || !container) return null;
    const cRect = container.getBoundingClientRect();
    const eRect = el.getBoundingClientRect();

    if (itemLayout === "equal" && contentEl) {
      const contentRect = contentEl.getBoundingClientRect();
      const horizontalInset = size === "sm" ? 18 : 22;
      return {
        left: contentRect.left - cRect.left - horizontalInset,
        width: contentRect.width + horizontalInset * 2,
      };
    }

    return { left: eRect.left - cRect.left, width: eRect.width };
  }, [itemLayout, items, size]);

  useLayoutEffect(() => {
    if (!displayTarget) {
      setRenderBubble(null);
      setAnimate(false);
      prevDisplayTarget.current = null;
      prevBubbleRect.current = null;
      return;
    }

    const pos = measureAt(displayTarget);
    if (!pos) return;

    const wasNull = prevDisplayTarget.current == null;
    const prevTarget = prevDisplayTarget.current;
    const moved = prevTarget != null && prevTarget !== displayTarget;
    const prevRect = prevBubbleRect.current;

    prevDisplayTarget.current = displayTarget;

    if (wasNull) {
      prevBubbleRect.current = pos;
      setRenderBubble(pos);
      setAnimate(false);
      requestAnimationFrame(() => requestAnimationFrame(() => setAnimate(true)));
      return;
    }

    prevBubbleRect.current = pos;
    setRenderBubble(pos);
    if (moved && animate && prevRect) triggerBounce();
  }, [displayTarget, measureAt, animate, triggerBounce]);

  useEffect(() => {
    if (!displayTarget) return;
    const ro = new ResizeObserver(() => {
      const pos = measureAt(displayTarget);
      if (pos) {
        prevBubbleRect.current = pos;
        setRenderBubble(pos);
      }
    });
    if (containerRef.current) ro.observe(containerRef.current);
    return () => ro.disconnect();
  }, [displayTarget, measureAt]);

  const padClass  = size === "sm" ? "px-3 py-[5px]"   : "px-3.5 py-[7px]";
  const textClass = size === "sm" ? "text-[11px]"      : "text-xs";

  return (
    <div ref={containerRef} className={cn("relative flex items-center gap-1.5 overflow-visible", className)}>
      <div
        ref={bubbleRef as React.Ref<HTMLDivElement>}
        aria-hidden
        className="lpg-bubble pointer-events-none absolute inset-y-0"
        style={{
          left: renderBubble?.left ?? 0,
          width: renderBubble?.width ?? 0,
          opacity: renderBubble ? 1 : 0,
          ...groupSquishStyle(bounceIntensity, deformAxis),
          transition: animate ? SLIDE_TRANSITION : "opacity 240ms ease",
        }}
      >
        <LiquidSurface
          as="div"
          variant={displayVariant}
          tone="tinted"
          shape="pill"
          size="free"
          interactive={false}
          cursorGlow={false}
          reactive
          bounceIntensity={bounceIntensity}
          style={{ position: "absolute", inset: 0 }}
        />
      </div>

      {items.map((item, idx) => {
        const isActive = item.value === activeValue;
        const variant  = resolveCircleActionVariant(item.variant ?? "slate");
        const vars     = circleActionVariantCssVars(variant);

        return (
          <button
            key={item.value}
            ref={(el) => { itemRefs.current[idx] = el; }}
            type="button"
            disabled={item.disabled}
            onClick={() => onChange?.(item.value)}
            onMouseEnter={() => {
              setHoveredValue(item.value);
              onHover?.(item.value);
            }}
            onMouseLeave={() => {
              setHoveredValue(null);
              onHover?.(null);
            }}
            style={vars as React.CSSProperties}
            className={cn(
              "lpg-item relative z-[1] inline-flex shrink-0 items-center justify-center gap-1.5 rounded-full",
              "select-none font-medium tracking-[-0.01em]",
              "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-400/60 focus-visible:ring-offset-1",
              "disabled:pointer-events-none disabled:opacity-40",
              "motion-safe:[transition:color_280ms_ease,transform_300ms_cubic-bezier(0.34,1.56,0.64,1)]",
              padClass,
              textClass,
              itemLayout === "equal" && "min-w-[8.75rem]",
              isActive ? "lpg-item--active" : "lpg-item--idle",
            )}
          >
            <span
              ref={(el) => { contentRefs.current[idx] = el; }}
              className="inline-flex items-center justify-center gap-1.5"
            >
              {item.icon && <span className="flex-shrink-0">{item.icon}</span>}
              {item.label != null && <span className="whitespace-nowrap">{item.label}</span>}
            </span>
          </button>
        );
      })}

      <style jsx global>{`
        @keyframes lpg-horizontal-bounce {
          0% {
            transform: scaleX(1) scaleY(1);
          }
          24% {
            transform:
              scaleX(calc(1 + 0.10 * var(--lp-squish-intensity, 1.55)))
              scaleY(calc(1 - 0.04 * var(--lp-squish-intensity, 1.55)));
          }
          52% {
            transform:
              scaleX(calc(1 - 0.045 * var(--lp-squish-intensity, 1.55)))
              scaleY(calc(1 + 0.025 * var(--lp-squish-intensity, 1.55)));
          }
          76% {
            transform:
              scaleX(calc(1 + 0.025 * var(--lp-squish-intensity, 1.55)))
              scaleY(calc(1 - 0.012 * var(--lp-squish-intensity, 1.55)));
          }
          100% {
            transform: scaleX(1) scaleY(1);
          }
        }

        @keyframes lpg-water-expand {
          from { clip-path: ellipse(48% 0% at 50% 0%); opacity: 0; }
          30%  { opacity: 1; }
          to   { clip-path: ellipse(140% 180% at 50% 0%); opacity: 1; }
        }
        .lpg-water-expand {
          animation: lpg-water-expand 520ms cubic-bezier(0.22,1,0.36,1) both;
        }

        .lpg-bubble .liquid-surface {
          box-shadow:
            inset -1px -1px 0 rgba(255,255,255,0.40),
            inset -1px  0   0 rgba(255,255,255,0.12),
            inset  0   -1px 0 rgba(255,255,255,0.20),
            inset  0    0   0 1.5px rgba(203,213,225,0.50),
            inset  1px  1px 0 rgba(15,23,42,0.03),
            0 1px 2px rgba(15,23,42,0.04),
            0 4px 12px -6px rgba(15,23,42,0.05);
        }
        .dark .lpg-bubble .liquid-surface {
          box-shadow:
            inset -1px -1px 0 rgba(255,255,255,0.003),
            inset -1px  0   0 rgba(255,255,255,0.001),
            inset  0   -1px 0 rgba(255,255,255,0.002),
            inset  0    0   0 1.5px rgba(100,116,139,0.06),
            inset  1px  1px 0 rgba(0,0,0,0.16),
            0 1px 3px rgba(0,0,0,0.10),
            0 4px 12px -4px rgba(0,0,0,0.16);
        }

        .lpg-item--idle        { color: rgb(100 116 139); }
        .dark .lpg-item--idle  { color: rgb(161 161 170); }
        .lpg-item--active      { color: var(--gb-accent-color); }
        .dark .lpg-item--active { color: var(--gb-accent-color-dark); }
        .lpg-item:not(:disabled):active { transform: scale(0.97); }
        .lpg-item--idle:not(:disabled):hover       { color: var(--gb-accent-color); }
        .dark .lpg-item--idle:not(:disabled):hover { color: var(--gb-accent-color-dark); }

        .lpg-item--idle img, .lpg-item--idle svg {
          filter: var(--cab-icon-filter-idle, none);
          opacity: 0.78;
          transition: filter 280ms ease, opacity 280ms ease;
        }
        .dark .lpg-item--idle img, .dark .lpg-item--idle svg {
          filter: var(--cab-icon-filter-idle-dark, none);
          opacity: 0.62;
        }
        .lpg-item--idle:not(:disabled):hover img,
        .lpg-item--idle:not(:disabled):hover svg {
          filter: var(--cab-icon-filter-idle, none);
          opacity: 0.90;
        }
        .dark .lpg-item--idle:not(:disabled):hover img,
        .dark .lpg-item--idle:not(:disabled):hover svg {
          filter: var(--cab-icon-filter-idle-dark, none);
          opacity: 0.85;
        }
        .lpg-item--active img, .lpg-item--active svg {
          filter: var(--cab-icon-filter-idle, none);
          opacity: 0.90;
          transition: filter 280ms ease, opacity 280ms ease;
        }
        .dark .lpg-item--active img, .lpg-item--active svg {
          filter: var(--cab-icon-filter-idle-dark, none);
          opacity: 0.85;
        }
      `}</style>
    </div>
  );
}
